'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
    ArrowRight,
    Download,
    Monitor,
    Smartphone,
    Cpu,
    Zap,
    Shield,
    Cloud,
    Terminal,
    CheckCircle,
    Apple,
    Bot,
    Mic,
    Home as HomeIcon,
    Calendar,
    Mail,
    Search,
    Globe
} from 'lucide-react'

export default function OrchestratorPage() {
    return (
        <div className="min-h-screen bg-gray-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                                unoptimized
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">For Robotics</Link>
                            <Link href="/orchestrator" className="text-[#4ade80] transition-colors text-sm font-medium">Orchestrator</Link>
                            <Link href="https://bb23llc.com" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">About BB23</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link
                                href="/login"
                                className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2"
                            >
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get Started Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a5f] via-gray-950 to-[#134e4a]" />
                <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#4ade80]/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#22c55e]/10 rounded-full blur-[100px]" />

                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full mb-8">
                        <Terminal className="w-4 h-4 text-[#4ade80]" />
                        <span className="text-gray-300 text-sm font-medium">Native Desktop & Mobile Agent</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
                        LinkBox
                        <br />
                        <span className="bg-gradient-to-r from-[#4ade80] via-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                            Orchestrator
                        </span>
                    </h1>

                    <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Your AI-powered desktop companion that connects LinkBox to your local machine.
                        Control apps, automate workflows, search files, and execute tasks with voice commands
                        — all from your Mac or iPhone.
                    </p>

                    {/* Download Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                        {/* macOS Download */}
                        <a
                            href="https://github.com/BB23-LLC/LinkBoxOrchestrator/releases/latest/download/LinkBoxOrchestrator.dmg"
                            className="inline-flex items-center justify-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all shadow-2xl min-w-[280px]"
                        >
                            <Apple className="w-6 h-6" />
                            <div className="text-left">
                                <div className="text-xs text-gray-500">Download for</div>
                                <div className="font-bold">macOS</div>
                            </div>
                            <Download className="w-5 h-5 ml-2" />
                        </a>

                        {/* iOS App Store */}
                        <a
                            href="https://apps.apple.com/app/id6753836843"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-3 bg-gray-800/50 backdrop-blur border border-gray-700 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-700/50 transition-all min-w-[280px]"
                        >
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                            </svg>
                            <div className="text-left">
                                <div className="text-xs text-gray-400">Download on the</div>
                                <div className="font-bold">App Store</div>
                            </div>
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </a>
                    </div>

                    {/* Platform icons */}
                    <div className="flex flex-wrap gap-6 justify-center items-center text-gray-400">
                        <div className="flex items-center gap-2">
                            <Monitor className="w-5 h-5" />
                            <span className="text-sm">macOS 13+</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Smartphone className="w-5 h-5" />
                            <span className="text-sm">iOS 17+</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            <span className="text-sm">Signed & Notarized</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-24 bg-gray-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-full mb-4">
                            <span className="text-[#4ade80] text-sm font-medium">Agent Capabilities</span>
                        </div>
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Your AI Agent, On Your Machine
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            LinkBox Orchestrator bridges cloud AI with local execution, giving you
                            unprecedented control over your digital life.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <FeatureCard
                            icon={<Mic className="w-6 h-6" />}
                            title="Voice Commands"
                            description="Talk to your Mac naturally. 'Open Figma and start a new project' just works."
                            gradient="from-emerald-500 to-teal-500"
                        />
                        <FeatureCard
                            icon={<Search className="w-6 h-6" />}
                            title="File Search"
                            description="Find any file on your Mac using natural language. Powered by Spotlight integration."
                            gradient="from-blue-500 to-cyan-500"
                        />
                        <FeatureCard
                            icon={<Terminal className="w-6 h-6" />}
                            title="App Control"
                            description="Launch, switch, and control any application. Automate repetitive workflows."
                            gradient="from-purple-500 to-pink-500"
                        />
                        <FeatureCard
                            icon={<HomeIcon className="w-6 h-6" />}
                            title="Smart Home"
                            description="Control your HomeKit devices directly from your Mac via voice or clicks."
                            gradient="from-amber-500 to-orange-500"
                        />
                        <FeatureCard
                            icon={<Calendar className="w-6 h-6" />}
                            title="Calendar & Tasks"
                            description="Create events, set reminders, and manage your schedule hands-free."
                            gradient="from-rose-500 to-red-500"
                        />
                        <FeatureCard
                            icon={<Mail className="w-6 h-6" />}
                            title="Email Drafts"
                            description="Compose and send emails using voice. AI drafts in your personal style."
                            gradient="from-indigo-500 to-violet-500"
                        />
                    </div>
                </div>
            </section>

            {/* Detailed Feature Write-ups */}
            <section className="py-24 bg-gray-950">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Comprehensive Feature Guide
                        </h2>
                        <p className="text-xl text-gray-400">
                            Everything LinkBox Orchestrator can do for you
                        </p>
                    </div>

                    <div className="space-y-16">
                        {/* Voice Commands & LinkBox Voice */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
                                    <Mic className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🎙️ Voice Commands with LinkBox Voice</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Talk to your Mac like you would a human assistant. LinkBox Orchestrator uses
                                Link Ultra Voice to deliver near-instant voice responses.
                                Ask complex questions, give multi-step commands, or have a natural conversation
                                — the AI maintains context throughout the entire session.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Sub-200ms voice response time using Link Ultra Voice",
                                    "Always-listening mode with customizable wake word ('Hey LinkBox')",
                                    "Full context awareness across the entire conversation session",
                                    "Multi-turn dialogue for complex, back-and-forth discussions",
                                    "Real-time transcription displayed as you speak",
                                    "Emotional intelligence that detects tone and adjusts responses"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Application Control */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                                    <Terminal className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🖥️ Application Control & Automation</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Control your entire Mac with voice or text commands. LinkBox Orchestrator
                                integrates deeply with macOS to launch, switch, and manage applications.
                                Create custom automation routines that execute multiple actions with a single command.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Launch any application by name: 'Open Safari', 'Start Slack'",
                                    "Switch between apps: 'Show me Finder', 'Go to VS Code'",
                                    "Execute keyboard shortcuts: 'Press Command+Shift+4'",
                                    "Automate workflows: 'Open my work apps' launches multiple apps",
                                    "Window management: 'Move this to the left half of the screen'",
                                    "Close and quit applications on command"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* File Search */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
                                    <Search className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🔍 Intelligent File Search</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Stop digging through folders. Describe what you're looking for in plain English
                                and LinkBox Orchestrator will find it. Powered by deep Spotlight integration,
                                searches are instant and can match both file names and content.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Natural language search: 'Find my tax documents from last year'",
                                    "Spotlight integration for blazing-fast results",
                                    "Content search: 'Find PDFs mentioning quarterly revenue'",
                                    "Smart filtering by date, type, size, and location",
                                    "Quick preview of found files without leaving the app",
                                    "One-click to open files or reveal in Finder"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Smart Home */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white">
                                    <HomeIcon className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🏠 Smart Home Control</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Your Mac becomes a smart home control center. Control lights, thermostats,
                                locks, and hundreds of other devices using voice commands or the Orchestrator
                                interface. Create custom scenes and automate your entire home.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "HomeKit integration for all Apple Home devices",
                                    "Voice control: 'Turn off the living room lights'",
                                    "Scene activation: 'Activate movie mode'",
                                    "Device status queries: 'Is the garage door closed?'",
                                    "Cross-platform sync with your LinkBox mobile app",
                                    "Google Home and Alexa support via LinkBox Cloud"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Calendar & Reminders */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center text-white">
                                    <Calendar className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">📅 Calendar & Task Management</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Manage your time without touching a keyboard. Create events, set reminders,
                                and check your schedule using natural language. LinkBox Orchestrator syncs
                                with all major calendar providers to keep you organized.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Create events: 'Schedule a meeting with John tomorrow at 3pm'",
                                    "Set reminders: 'Remind me to call mom in 2 hours'",
                                    "Query schedule: 'What's on my calendar this week?'",
                                    "Conflict detection warns about double-bookings",
                                    "Integration with Apple Calendar, Google, and Outlook",
                                    "Natural date parsing: 'next Tuesday', 'day after tomorrow'"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Email */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white">
                                    <Mail className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">📧 Email Drafting & Management</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Turn voice into polished emails. Dictate a brief description and LinkBox
                                Orchestrator drafts a professional email in your personal style. Review,
                                edit, and send — all without typing a single word.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Voice-to-email: 'Send an email to Sarah about the project'",
                                    "AI drafting that matches your personal writing style",
                                    "Read emails aloud: 'Read my latest unread emails'",
                                    "Quick replies: 'Reply saying I'll be there at 5'",
                                    "Integration with Apple Mail, Gmail, and Outlook",
                                    "Smart prioritization for important messages"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Web Search & Knowledge */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white">
                                    <Globe className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🌐 Web Search & Knowledge Base</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Combine the power of web search with your personal knowledge base. Ask questions
                                and get answers that blend real-time information with context from your saved
                                notes, documents, and memories in LinkBox.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Real-time web search: 'What's the weather tomorrow?'",
                                    "Access your LinkBox knowledge base from desktop",
                                    "RAG-powered answers grounded in your personal data",
                                    "Save web pages to LinkBox with one command",
                                    "Cross-platform sync with mobile and web apps",
                                    "AI summaries of search results and articles"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Desktop Automation */}
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center text-white">
                                    <Bot className="w-7 h-7" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">🤖 Advanced Automation & Scripting</h3>
                            </div>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                For power users, LinkBox Orchestrator supports advanced automation including
                                shell scripts, AppleScript, and integration with Claude Desktop. Create custom
                                workflows that automate complex multi-step tasks with a single command.
                            </p>
                            <ul className="grid md:grid-cols-2 gap-3">
                                {[
                                    "Execute shell commands and scripts via voice",
                                    "AppleScript integration for deep macOS automation",
                                    "Keyboard and mouse automation for workflows",
                                    "Scheduled tasks: 'Run backup script at midnight'",
                                    "Custom aliases: 'Work mode' → opens apps, sets DND",
                                    "Integration with Claude Desktop for AI tasks"
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-400">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 bg-gray-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">
                            How It Works
                        </h2>
                        <p className="text-xl text-gray-400">
                            Seamless integration between cloud and desktop
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#4ade80] to-[#22c55e] flex items-center justify-center">
                                <Cloud className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">1. Cloud Brain</h3>
                            <p className="text-gray-400">
                                Your knowledge base, memories, and AI processing live securely in LinkBox Cloud.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                                <Zap className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">2. Local Bridge</h3>
                            <p className="text-gray-400">
                                Orchestrator connects cloud intelligence to your Mac's capabilities.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                <Cpu className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">3. Native Execution</h3>
                            <p className="text-gray-400">
                                Tasks are executed natively on your machine with full system access.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* System Requirements */}
            <section className="py-24 bg-gray-900/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-4">
                            System Requirements
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* macOS */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center">
                                    <Monitor className="w-6 h-6 text-[#4ade80]" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">macOS</h3>
                                    <p className="text-gray-400 text-sm">Desktop Application</p>
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    macOS 13 Ventura or later
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    Apple Silicon (M1/M2/M3) or Intel
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    4 GB RAM minimum
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    100 MB disk space
                                </li>
                            </ul>
                            <a
                                href="https://github.com/BB23-LLC/LinkBoxOrchestrator/releases/latest/download/LinkBoxOrchestrator.dmg"
                                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#4ade80] text-gray-900 py-3 rounded-lg font-semibold hover:bg-[#86efac] transition-all"
                            >
                                <Download className="w-5 h-5" />
                                Download for Mac
                            </a>
                        </div>

                        {/* iOS */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center">
                                    <Smartphone className="w-6 h-6 text-[#4ade80]" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">iOS</h3>
                                    <p className="text-gray-400 text-sm">Mobile App</p>
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    iOS 17 or later
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    iPhone or iPad
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    Microphone access for voice
                                </li>
                                <li className="flex items-center text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] mr-3 flex-shrink-0" />
                                    Internet connection required
                                </li>
                            </ul>
                            <a
                                href="https://apps.apple.com/app/id6753836843"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-all"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                </svg>
                                Download on App Store
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-r from-[#1e3a5f] to-[#134e4a]">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Ready to Automate Your Mac?
                    </h2>
                    <p className="text-xl text-gray-300 mb-10">
                        Download LinkBox Orchestrator and unlock the full power of AI on your desktop.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://github.com/BB23-LLC/LinkBoxOrchestrator/releases/latest/download/LinkBoxOrchestrator.dmg"
                            className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all"
                        >
                            <Download className="w-5 h-5" />
                            Download for macOS
                        </a>
                        <Link
                            href="/"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all"
                        >
                            Learn More About LinkBox
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-950 border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={32}
                                height={32}
                                className="rounded-lg"
                                unoptimized
                            />
                            <span className="text-sm text-gray-400">
                                © 2026 BB23 LLC. All rights reserved.
                            </span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link href="/" className="text-gray-400 hover:text-white text-sm transition-colors">Home</Link>
                            <Link href="/features" className="text-gray-400 hover:text-white text-sm transition-colors">Features</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white text-sm transition-colors">Robotics</Link>
                            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link>
                            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}

function FeatureCard({
    icon,
    title,
    description,
    gradient,
}: {
    icon: React.ReactNode
    title: string
    description: string
    gradient: string
}) {
    return (
        <div className="group bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all hover:-translate-y-1">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white mb-4`}>
                {icon}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-400">{description}</p>
        </div>
    )
}
